import React, { useState, useEffect } from 'react';
import { X, GripVertical, Eye, EyeOff, Save, RotateCcw, AlertCircle, ChevronUp, ChevronDown } from './icons/HeroshhiIcons';

// ============================================
// COLUMN LAYOUT EDITOR (create / edit a saved layout)
// Reorder: drag (computers) or ↑ ↓ (everywhere, incl. phones)
// ============================================
// Starting set for a NEW layout and for "Reset to default". Saved layouts keep their own columns.
export const DEFAULT_COLUMNS = [
  { key: 'whyNow', label: 'Why now', visible: true, order: 1 },
  { key: 'lastOutcome', label: 'Last outcome', visible: true, order: 2 },
  { key: 'nextFollowUpDate', label: 'Next follow-up', visible: true, order: 3 },
  { key: 'sourceCampaign', label: 'Source and campaign', visible: true, order: 4 },
  { key: 'financial.spenderCategory', label: 'Spender tier', visible: true, order: 5 },
  { key: 'status', label: 'Status', visible: false, order: 6 },
  { key: 'assignedTo', label: 'Assigned to', visible: false, order: 7 },
  { key: 'callStatus', label: 'Call status', visible: false, order: 8 },
  { key: 'lastInteractionAt', label: 'Last active', visible: false, order: 9 },
  { key: 'lastContactedAt', label: 'Last contacted', visible: false, order: 10 },
  { key: 'segment', label: 'Segment', visible: false, order: 11 },
  { key: 'stage', label: 'Stage', visible: false, order: 12 },
  { key: 'health', label: 'Health', visible: false, order: 13 },
  { key: 'leadType', label: 'Lead type', visible: false, order: 14 },
  { key: 'createdAt', label: 'Created date', visible: false, order: 15 },
  { key: 'campaignName', label: 'Campaign name', visible: false, order: 16 },
  { key: 'adGroupName', label: 'Ad group', visible: false, order: 17 },
  { key: 'adName', label: 'Ad name', visible: false, order: 18 },
  { key: 'source', label: 'Source', visible: false, order: 19 },
  { key: 'city', label: 'City', visible: false, order: 20 },
  { key: 'email', label: 'Email', visible: false, order: 21 },
  { key: 'tags', label: 'Tags', visible: false, order: 22 },
];

// A saved layout made before a column existed still offers it (hidden) at the end
const withAllColumns = (cols) => {
  const have = new Set(cols.map(c => c.key));
  const extra = DEFAULT_COLUMNS.filter(c => !have.has(c.key)).map((c, i) => ({ ...c, visible: false, order: cols.length + i + 1 }));
  return [...cols, ...extra];
};

export default function ColumnCustomizer({ isOpen, onClose, currentView, onSave, onCreateNew, isNewView = false }) {
  const [viewName, setViewName] = useState('');
  const [columns, setColumns] = useState([]);
  const [setAsDefault, setSetAsDefault] = useState(false);
  const [draggedItem, setDraggedItem] = useState(null);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    if (isNewView || !currentView || currentView.builtin) {
      setViewName(currentView?.builtin ? `My ${currentView.name.toLowerCase()} layout` : '');
      setColumns(currentView?.builtin ? withAllColumns(currentView.columns) : DEFAULT_COLUMNS);
      setSetAsDefault(false);
    } else {
      setViewName(currentView.name);
      setColumns(withAllColumns([...currentView.columns].sort((a, b) => a.order - b.order)));
      setSetAsDefault(currentView.isDefault);
    }
    setError('');
  }, [isOpen, currentView, isNewView]);

  const renumber = (cols) => cols.map((c, i) => ({ ...c, order: i + 1 }));
  const toggleColumnVisibility = (key) => setColumns(prev => prev.map(col => col.key === key ? { ...col, visible: !col.visible } : col));
  const move = (index, dir) => setColumns(prev => {
    const j = index + dir; if (j < 0 || j >= prev.length) return prev;
    const next = [...prev]; [next[index], next[j]] = [next[j], next[index]]; return renumber(next);
  });

  const handleDragStart = (e, index) => { setDraggedItem(index); e.dataTransfer.effectAllowed = 'move'; e.currentTarget.style.opacity = '0.5'; };
  const handleDragEnd = (e) => { e.currentTarget.style.opacity = '1'; setDraggedItem(null); };
  const handleDragOver = (e) => e.preventDefault();
  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    if (draggedItem === null || draggedItem === dropIndex) return;
    const next = [...columns]; const [d] = next.splice(draggedItem, 1); next.splice(dropIndex, 0, d);
    setColumns(renumber(next)); setDraggedItem(null);
  };

  // Creating from a built-in layout = a new saved layout
  const creating = isNewView || !currentView || currentView.builtin;
  const handleSave = async () => {
    if (!viewName.trim()) { setError('Please enter a name for this layout'); return; }
    setSaving(true); setError('');
    try {
      if (creating) await onCreateNew({ name: viewName.trim(), columns, setAsDefault });
      else await onSave({ name: viewName.trim(), columns, setAsDefault });
      onClose();
    } catch (err) { setError(err.message || 'Failed to save layout'); }
    finally { setSaving(false); }
  };

  const visibleCount = columns.filter(c => c.visible).length;
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-end md:items-center justify-center md:p-4 z-50">
      <div className="bg-white rounded-t-2xl md:rounded-2xl shadow-2xl w-full md:max-w-2xl max-h-[92dvh] overflow-hidden flex flex-col">
        <div className="px-5 py-4 border-b flex justify-between items-center gap-3">
          <div className="min-w-0">
            <h3 className="text-lg md:text-xl font-extrabold text-[#10203A]">{creating ? 'New column layout' : 'Edit column layout'}</h3>
            <p className="text-sm text-gray-600 truncate">{creating ? (currentView?.builtin ? `Starting from "${currentView.name}"` : 'Choose and order the columns') : `Editing "${currentView?.name}"`}</p>
          </div>
          <button type="button" onClick={onClose} aria-label="Close" className="min-h-[44px] min-w-[44px] flex items-center justify-center rounded-xl border border-gray-300 text-[#10203A]"><X size={20} /></button>
        </div>

        {error && <div className="mx-5 mt-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm flex items-center gap-2"><AlertCircle size={18} />{error}</div>}

        <div className="flex-1 overflow-y-auto p-5 space-y-5">
          <div>
            <label htmlFor="cc-name" className="block text-sm font-bold text-[#10203A] mb-1.5">Layout name</label>
            <input id="cc-name" type="text" value={viewName} onChange={(e) => setViewName(e.target.value)} placeholder="e.g. Morning calls, Clinic view"
              className="w-full min-h-[44px] px-3 border border-gray-300 rounded-lg" maxLength={50}
              disabled={currentView?.name === 'Default' && !creating} />
          </div>
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-bold text-[#10203A]">Columns ({visibleCount} shown)</span>
              <button type="button" onClick={() => setColumns(DEFAULT_COLUMNS)} className="min-h-[36px] text-sm font-semibold text-[#10203A] flex items-center gap-1 underline underline-offset-4 decoration-gray-300">
                <RotateCcw size={14} /> Reset to default
              </button>
            </div>
            <p className="text-xs text-gray-600 mb-2">The lead's name, phone and interest always show first. Use the eye to show or hide a column, and the arrows (or drag) to change the order.</p>
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              {columns.map((column, index) => (
                <div key={column.key} draggable onDragStart={(e) => handleDragStart(e, index)} onDragEnd={handleDragEnd} onDragOver={handleDragOver} onDrop={(e) => handleDrop(e, index)}
                  className={`flex items-center gap-2 pl-2 pr-2 py-1.5 border-b last:border-b-0 ${draggedItem === index ? 'bg-slate-50' : 'bg-white'} ${!column.visible ? 'opacity-60' : ''}`}>
                  <GripVertical size={18} className="text-gray-400 hidden md:block cursor-move" />
                  <span className={`flex-1 min-w-0 truncate font-semibold ${column.visible ? 'text-[#10203A]' : 'text-gray-500'}`}>{column.label}</span>
                  <button type="button" onClick={() => move(index, -1)} disabled={index === 0} aria-label={`Move ${column.label} up`} className="min-h-[38px] min-w-[38px] flex items-center justify-center rounded-lg text-gray-600 hover:bg-gray-100 disabled:opacity-30"><ChevronUp size={18} /></button>
                  <button type="button" onClick={() => move(index, 1)} disabled={index === columns.length - 1} aria-label={`Move ${column.label} down`} className="min-h-[38px] min-w-[38px] flex items-center justify-center rounded-lg text-gray-600 hover:bg-gray-100 disabled:opacity-30"><ChevronDown size={18} /></button>
                  <button type="button" onClick={() => toggleColumnVisibility(column.key)} aria-label={column.visible ? `Hide ${column.label}` : `Show ${column.label}`}
                    className={`min-h-[38px] min-w-[38px] flex items-center justify-center rounded-lg ${column.visible ? 'bg-[#10203A] text-white' : 'bg-gray-100 text-gray-500'}`}>
                    {column.visible ? <Eye size={17} /> : <EyeOff size={17} />}
                  </button>
                </div>
              ))}
            </div>
          </div>
          <label className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg cursor-pointer">
            <input type="checkbox" checked={setAsDefault} onChange={(e) => setSetAsDefault(e.target.checked)} className="w-5 h-5 rounded accent-[#10203A]" />
            <span><span className="block font-bold text-[#10203A]">Open with this layout</span><span className="text-sm text-gray-600">Use it automatically next time</span></span>
          </label>
        </div>

        <div className="px-5 py-3 border-t bg-gray-50 flex justify-end gap-2" style={{ paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom))' }}>
          <button type="button" onClick={onClose} className="min-h-[44px] px-4 rounded-xl font-bold text-gray-600 hover:bg-gray-100">Cancel</button>
          <button type="button" onClick={handleSave} disabled={saving || !viewName.trim() || visibleCount === 0}
            className="min-h-[44px] px-5 bg-[#10203A] text-white rounded-xl font-bold disabled:opacity-50 flex items-center gap-2">
            {saving ? 'Saving…' : <><Save size={16} /> {creating ? 'Create layout' : 'Save changes'}</>}
          </button>
        </div>
      </div>
    </div>
  );
}